import React, { useEffect, useRef } from 'react';

const rand = (a, b) => a + Math.random() * (b - a);
const pick = (list) => list[Math.floor(Math.random() * list.length)];
const mix = (a, b, t) => Math.round(a + (b - a) * t);

export default function GridBeams({ className = '', cell = 56, color = '45, 203, 164', line = 'rgba(11, 15, 14, 0.065)', beams = 3, radius = 240, slots = [], avoid = '', minBand = 230, onLayout }) {
  const ref = useRef(null);
  const report = useRef(onLayout);

  useEffect(() => {
    report.current = onLayout;
  });

  useEffect(() => {
    const canvas = ref.current;
    const host = canvas && canvas.parentElement;
    const ctx = canvas && canvas.getContext('2d');
    if (!host || !ctx) return undefined;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const fine = window.matchMedia('(any-pointer: fine)').matches;
    let w = 0;
    let h = 0;
    let size = cell;
    let xs = [];
    let ys = [];
    let sideXs = [];
    let cells = [];
    let base = null;
    let grid = null;
    let list = [];
    let raf = 0;
    let last = 0;
    let born = 0;
    let cooldown = 0.6;
    let pingIn = 2.4;
    let visible = true;
    let alive = true;
    const mouse = { x: 0, y: 0, tx: 0, ty: 0, a: 0, ta: 0 };

    const extent = (r) => {
      if (!avoid) return w * 0.3;
      let far = 0;
      host.querySelectorAll(avoid).forEach((el) => {
        const b = el.getBoundingClientRect();
        if (!b.width) return;
        far = Math.max(far, Math.abs(b.left - r.left - w / 2), Math.abs(b.right - r.left - w / 2));
      });
      return far;
    };

    const layoutSlots = (r) => {
      cells = [];
      if (slots.length) {
        const ci = xs.reduce((best, x, i) => (Math.abs(x - w / 2) < Math.abs(xs[best] - w / 2) ? i : best), 0);
        const rj = ys.reduce((best, y, i) => (Math.abs(y - h * 0.46) < Math.abs(ys[best] - h * 0.46) ? i : best), 0);
        const inner = extent(r) + 36;
        const outer = w / 2 - 32;
        const placed = [];
        slots.every((s, k) => {
          const cw = s.cw || 3;
          const ch = s.ch || 2;
          if (outer - inner < Math.max(minBand, cw * size + 24)) return false;
          let col = Math.round(((inner + outer) / 2 - (cw * size) / 2) / size);
          col = Math.max(col, Math.ceil(inner / size));
          if ((col + cw) * size > outer) return false;
          const i0 = s.side < 0 ? ci - col - cw : ci + col;
          const j0 = rj + s.row;
          if (i0 < 0 || i0 + cw >= xs.length || j0 < 0 || j0 + ch >= ys.length) return false;
          const y0 = ys[j0];
          const y1 = ys[j0 + ch];
          if (y0 < 96 || y1 > h - 110) return false;
          placed.push({ x0: xs[i0], x1: xs[i0 + cw], y0, y1, g: 0, delay: 1.05 + k * 0.12 });
          return true;
        });
        if (placed.length === slots.length) cells = placed;
      }
      if (report.current) report.current(cells.map((c) => ({ x: c.x0 - 0.5, y: c.y0 - 0.5, w: c.x1 - c.x0, h: c.y1 - c.y0 })));
    };

    const build = () => {
      const r = host.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = r.width;
      h = r.height;
      size = w < 700 ? Math.round(cell * 0.78) : cell;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const ox = (w / 2) % size;
      const oy = (h * 0.46) % size;
      xs = [];
      ys = [];
      for (let x = ox; x <= w; x += size) xs.push(Math.round(x) + 0.5);
      for (let y = oy; y <= h; y += size) ys.push(Math.round(y) + 0.5);
      sideXs = xs.filter((x) => Math.abs(x - w / 2) > w * (w < 700 ? 0.3 : 0.24));
      grid = new Path2D();
      xs.forEach((x) => { grid.moveTo(x, 0); grid.lineTo(x, h); });
      ys.forEach((y) => { grid.moveTo(0, y); grid.lineTo(w, y); });
      base = document.createElement('canvas');
      base.width = canvas.width;
      base.height = canvas.height;
      const b = base.getContext('2d');
      b.setTransform(dpr, 0, 0, dpr, 0, 0);
      b.strokeStyle = line;
      b.lineWidth = 1;
      b.stroke(grid);
      layoutSlots(r);
      const mark = 5;
      b.strokeStyle = 'rgba(11, 15, 14, 0.22)';
      const cx = xs.reduce((best, x) => (Math.abs(x - w / 2) < Math.abs(best - w / 2) ? x : best), xs[0] || 0);
      const cols = cells.length ? [] : [xs[xs.indexOf(cx) - 5], xs[xs.indexOf(cx) + 5]].filter(Boolean);
      const rowsAt = [ys[1], ys[ys.length - 2]].filter(Boolean);
      cols.forEach((x) => rowsAt.forEach((y) => {
        b.beginPath();
        b.moveTo(x - mark, y);
        b.lineTo(x + mark, y);
        b.moveTo(x, y - mark);
        b.lineTo(x, y + mark);
        b.stroke();
      }));
    };

    const drawSlots = (age) => {
      cells.forEach((c) => {
        const a = reduce ? 1 : Math.max(0, Math.min(1, (age - c.delay) / 0.8));
        if (!a) return;
        const g = c.g;
        const sw = c.x1 - c.x0;
        const sh = c.y1 - c.y0;
        ctx.save();
        ctx.globalAlpha = a;
        ctx.fillStyle = `rgb(${mix(255, 238, g)}, ${mix(255, 251, g)}, ${mix(255, 247, g)})`;
        ctx.fillRect(c.x0, c.y0, sw, sh);
        if (g > 0.02) {
          ctx.shadowColor = `rgba(${color}, ${0.5 * g})`;
          ctx.shadowBlur = 16 * g;
        }
        ctx.strokeStyle = `rgba(${mix(11, 45, g)}, ${mix(15, 203, g)}, ${mix(14, 164, g)}, ${0.13 + 0.67 * g})`;
        ctx.lineWidth = 1;
        ctx.strokeRect(c.x0, c.y0, sw, sh);
        ctx.shadowBlur = 0;
        ctx.strokeStyle = 'rgba(11, 15, 14, 0.42)';
        ctx.beginPath();
        [[c.x0, c.y0], [c.x1, c.y0], [c.x0, c.y1], [c.x1, c.y1]].forEach(([x, y]) => {
          ctx.moveTo(x - 6, y);
          ctx.lineTo(x + 6, y);
          ctx.moveTo(x, y - 6);
          ctx.lineTo(x, y + 6);
        });
        ctx.stroke();
        ctx.restore();
      });
    };

    const spawn = () => {
      const dir = Math.random() < 0.5 ? 1 : -1;
      const len = rand(90, 170);
      const speed = rand(110, 220);
      const aimed = cells.length && Math.random() < 0.55 ? pick(cells) : null;
      const vertical = Math.random() < 0.5;
      if (vertical && (aimed || sideXs.length)) {
        const x = aimed ? (Math.random() < 0.5 ? aimed.x0 : aimed.x1) : pick(sideXs);
        list.push({ v: true, c: x, p: dir > 0 ? -len : h + len, dir, len, speed, end: dir > 0 ? h + len : -len });
      } else if (ys.length) {
        const y = aimed ? (Math.random() < 0.5 ? aimed.y0 : aimed.y1) : pick(ys);
        list.push({ v: false, c: y, p: dir > 0 ? -len : w + len, dir, len, speed, end: dir > 0 ? w + len : -len });
      }
    };

    const touch = (b, head) => {
      cells.forEach((c) => {
        if (b.v ? (Math.abs(c.x0 - b.c) < 1 || Math.abs(c.x1 - b.c) < 1) && head >= c.y0 && head <= c.y1 : (Math.abs(c.y0 - b.c) < 1 || Math.abs(c.y1 - b.c) < 1) && head >= c.x0 && head <= c.x1) c.g = 1;
      });
    };

    const draw = (now) => {
      if (!born) born = now;
      const age = (now - born) / 1000;
      const dt = last ? Math.min(0.064, (now - last) / 1000) : 0.016;
      last = now;
      ctx.clearRect(0, 0, w, h);
      ctx.drawImage(base, 0, 0, w, h);

      mouse.x += (mouse.tx - mouse.x) * 0.14;
      mouse.y += (mouse.ty - mouse.y) * 0.14;
      mouse.a += (mouse.ta - mouse.a) * 0.08;
      if (mouse.a > 0.01) {
        const g = ctx.createRadialGradient(mouse.x, mouse.y, 0, mouse.x, mouse.y, radius);
        g.addColorStop(0, `rgba(${color}, ${0.65 * mouse.a})`);
        g.addColorStop(1, `rgba(${color}, 0)`);
        ctx.strokeStyle = g;
        ctx.lineWidth = 1;
        ctx.stroke(grid);
      }

      pingIn -= dt;
      if (beams && pingIn <= 0 && cells.length) {
        pick(cells).g = 1;
        pingIn = rand(2.6, 4.4);
      }
      cells.forEach((c) => {
        const cx = (c.x0 + c.x1) / 2;
        const cy = (c.y0 + c.y1) / 2;
        const near = mouse.a > 0.01 ? Math.max(0, 1 - Math.hypot(mouse.x - cx, mouse.y - cy) / (radius * 0.8)) * 0.8 * mouse.a : 0;
        c.g = Math.max(near, c.g - dt * 0.8);
      });

      cooldown -= dt;
      if (cooldown <= 0 && list.length < beams) {
        spawn();
        cooldown = rand(0.5, 1.6);
      }
      list = list.filter((b) => {
        b.p += b.dir * b.speed * dt;
        const head = b.p;
        const tail = b.p - b.dir * b.len;
        touch(b, head);
        const g = b.v ? ctx.createLinearGradient(0, tail, 0, head) : ctx.createLinearGradient(tail, 0, head, 0);
        g.addColorStop(0, `rgba(${color}, 0)`);
        g.addColorStop(1, `rgba(${color}, 0.9)`);
        ctx.strokeStyle = g;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        if (b.v) {
          ctx.moveTo(b.c, tail);
          ctx.lineTo(b.c, head);
        } else {
          ctx.moveTo(tail, b.c);
          ctx.lineTo(head, b.c);
        }
        ctx.stroke();
        const hx = b.v ? b.c : head;
        const hy = b.v ? head : b.c;
        const glow = ctx.createRadialGradient(hx, hy, 0, hx, hy, 9);
        glow.addColorStop(0, `rgba(${color}, 0.55)`);
        glow.addColorStop(1, `rgba(${color}, 0)`);
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(hx, hy, 9, 0, Math.PI * 2);
        ctx.fill();
        return b.dir > 0 ? tail < b.end : tail > b.end;
      });

      drawSlots(age);
      raf = visible ? requestAnimationFrame(draw) : 0;
    };

    const paintStatic = () => {
      ctx.clearRect(0, 0, w, h);
      ctx.drawImage(base, 0, 0, w, h);
      drawSlots(99);
    };

    const start = () => {
      if (reduce || raf) return;
      last = 0;
      raf = requestAnimationFrame(draw);
    };

    const onMove = (e) => {
      const r = host.getBoundingClientRect();
      mouse.tx = e.clientX - r.left;
      mouse.ty = e.clientY - r.top;
      const inside = mouse.ty >= 0 && mouse.ty <= r.height;
      if (inside && mouse.ta === 0) {
        mouse.x = mouse.tx;
        mouse.y = mouse.ty;
      }
      mouse.ta = inside ? 1 : 0;
    };
    const onLeave = () => { mouse.ta = 0; };

    build();
    if (reduce) paintStatic();
    let resizeRaf = 0;
    const rebuild = () => {
      cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(() => {
        if (!alive) return;
        build();
        list = [];
        if (reduce) paintStatic();
      });
    };
    const ro = new ResizeObserver(rebuild);
    ro.observe(host);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(rebuild);
    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible) start();
    });
    io.observe(host);
    if (fine && !reduce) {
      window.addEventListener('pointermove', onMove, { passive: true });
      document.addEventListener('pointerleave', onLeave);
    }
    start();

    return () => {
      alive = false;
      ro.disconnect();
      io.disconnect();
      cancelAnimationFrame(raf);
      cancelAnimationFrame(resizeRaf);
      window.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerleave', onLeave);
    };
  }, [cell, color, line, beams, radius, slots, avoid, minBand]);

  return <canvas ref={ref} className={`ob-grid-beams ${className}`} aria-hidden="true" />;
}
